/**
 * Utility functions for converting weekly schedule between frontend and backend formats
 * 
 * Backend Format:
 * - Open/close times in 24-hour format
 * - Commas separate open/close pairs: "09:00,13:00"
 * - Pipe separates multiple periods: "09:00,13:00|14:00,18:00"
 * - Use "00:00,00:01" for fully closed days
 * 
 * Frontend Format:
 * - JSON array of DaySchedule objects with 12-hour time format
 */

export interface TimeRange {
    id: string;
    startTime: string; // 12-hour format: "9:00 AM"
    endTime: string;   // 12-hour format: "5:00 PM"
}

export interface DaySchedule {
    day: string;
    isOpen: boolean;
    timeRanges: TimeRange[];
}

/**
 * Convert 12-hour time to 24-hour format
 * Example: "1:00 PM" -> "13:00"
 */
export const convert12HourTo24Hour = (time12: string): string => {
    const [time, period] = time12.trim().split(' ');
    const [hourStr, minute] = time.split(':');
    let hour = parseInt(hourStr, 10);

    if (period === 'PM' && hour !== 12) {
        hour += 12;
    } else if (period === 'AM' && hour === 12) {
        hour = 0;
    }

    return `${hour.toString().padStart(2, '0')}:${minute}`;
};

/**
 * Convert 24-hour time to 12-hour format
 * Example: "13:00" -> "1:00 PM"
 */
export const convert24HourTo12Hour = (time24: string): string => {
    const [hourStr, minute] = time24.split(':');
    let hour = parseInt(hourStr, 10);
    const period = hour >= 12 ? 'PM' : 'AM';

    if (hour === 0) {
        hour = 12;
    } else if (hour > 12) {
        hour -= 12;
    }

    return `${hour}:${minute} ${period}`;
};

/**
 * Convert a single day's schedule to backend format
 * Examples:
 * - Closed: "00:00,00:01"
 * - Single period: "09:00,17:00"
 * - Multiple periods: "09:00,12:00|14:00,18:00"
 */
export const dayScheduleToBackendFormat = (daySchedule: DaySchedule): string => {
    // Closed day
    if (!daySchedule.isOpen || daySchedule.timeRanges.length === 0) {
        return '00:00,00:01';
    }

    // Convert each time range to 24-hour format and join with commas
    const periods = daySchedule.timeRanges.map(tr => {
        const start24 = convert12HourTo24Hour(tr.startTime);
        const end24 = convert12HourTo24Hour(tr.endTime);
        return `${start24},${end24}`;
    });

    // Join multiple periods with pipe
    return periods.join('|');
};

/**
 * Parse backend format for a single day to DaySchedule
 * Examples:
 * - "00:00,00:01" -> Closed
 * - "09:00,17:00" -> Open 9am-5pm
 * - "09:00,12:00|14:00,18:00" -> Open 9am-12pm and 2pm-6pm
 */
export const backendFormatToDaySchedule = (day: string, backendStr: string): DaySchedule => {
    // Check if closed
    if (backendStr === '00:00,00:01') {
        return {
            day,
            isOpen: false,
            timeRanges: []
        };
    }

    // Split by pipe for multiple periods
    const periods = backendStr.split('|');
    const timeRanges: TimeRange[] = periods.map((period, index) => {
        const [start, end] = period.split(',');
        return {
            id: `time_${Date.now()}_${index}_${Math.floor(Math.random() * 1e6)}`,
            startTime: convert24HourTo12Hour(start.trim()),
            endTime: convert24HourTo12Hour(end.trim())
        };
    });

    return {
        day,
        isOpen: true,
        timeRanges
    };
};

/**
 * Convert weekly schedule to backend format
 * Returns an array of 7 strings (Monday through Sunday)
 */
export const weeklyScheduleToBackend = (schedule: DaySchedule[]): string[] => {
    const DAYS_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    return DAYS_ORDER.map(day => {
        const daySchedule = schedule.find(d => d.day === day);
        if (!daySchedule) {
            return '00:00,00:01'; // Default to closed if day not found
        }
        return dayScheduleToBackendFormat(daySchedule);
    });
};

/**
 * Parse backend format array to weekly schedule
 * Expects array of 7 strings (Monday through Sunday)
 */
export const backendToWeeklySchedule = (backendArray: string[]): DaySchedule[] => {
    const DAYS_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    if (!backendArray || backendArray.length !== 7) {
        // Return default closed schedule if invalid
        return DAYS_ORDER.map(day => ({
            day,
            isOpen: false,
            timeRanges: []
        }));
    }

    return DAYS_ORDER.map((day, index) => {
        return backendFormatToDaySchedule(day, backendArray[index]);
    });
};

/**
 * Convert frontend JSON string to backend format
 */
export const convertWeeklyScheduleToBackend = (jsonStr: string): string[] => {
    try {
        const schedule: DaySchedule[] = JSON.parse(jsonStr);
        return weeklyScheduleToBackend(schedule);
    } catch {
        // Return default closed schedule if parsing fails
        return Array(7).fill('00:00,00:01');
    }
};

/**
 * Convert backend format to frontend JSON string
 */
export const convertWeeklyScheduleFromBackend = (backendArray: string[]): string => {
    const schedule = backendToWeeklySchedule(backendArray);
    return JSON.stringify(schedule);
};

/**
 * Validate backend format for a single day
 */
export const isValidDayFormat = (str: string): boolean => {
    if (!str) return false;

    // Check for closed format
    if (str === '00:00,00:01') return true;

    // Check for time periods format
    const periodPattern = /^\d{2}:\d{2},\d{2}:\d{2}$/;
    const periods = str.split('|');

    return periods.every(period => periodPattern.test(period.trim()));
};

/**
 * Merge an overnight overflow prefix into an existing day's backend value.
 *
 * When the previous day has an overnight range (e.g. 09:00→02:00), its
 * midnight-rollover portion ("00:00,02:00") must be prepended to the next
 * day's own hours. Any existing midnight-overflow periods (periods whose
 * start time is "00:00") are first stripped from the existing value so that
 * re-saves don't accumulate stale prefixes.
 *
 * Examples:
 *   mergeOvernightPrefix("00:00,02:00", "09:00,23:59")
 *     → "00:00,02:00|09:00,23:59"
 *
 *   mergeOvernightPrefix("00:00,02:00", "00:00,01:00|09:00,23:59")
 *     → "00:00,02:00|09:00,23:59"  (stale 00:00 prefix replaced)
 *
 *   mergeOvernightPrefix("00:00,02:00", undefined)
 *     → "00:00,02:00"
 *
 *   mergeOvernightPrefix("00:00,02:00", "00:00,00:01")
 *     → "00:00,02:00"              (closed sentinel is replaced entirely)
 */
export const mergeOvernightPrefix = (
    overnightOverflow: string,
    existingNextDayValue: string | undefined
): string => {
    // No existing value or closed sentinel — just return the overflow
    if (
        !existingNextDayValue ||
        existingNextDayValue === '00:00,00:01'
    ) {
        return overnightOverflow;
    }

    // Strip only previously-written overnight-overflow periods (start === "00:00",
    // end > "00:01", and end <= "06:00") so that legitimate midnight-opening
    // business hours (e.g. "00:00,07:00") are preserved.
    const ownPeriods = existingNextDayValue
        .split('|')
        .filter(period => {
            const [rawStart, rawEnd] = period.split(',');
            const start = rawStart?.trim();
            const end = rawEnd?.trim();
            return !(
                start === '00:00' &&
                end &&
                end > '00:01' &&
                end <= '06:00'
            );
        });

    const parts = [overnightOverflow, ...ownPeriods].filter(Boolean);
    return parts.join('|');
};

/**
 * Split a single-day backend value string into the portion that belongs to
 * the current day and the overflow that spills into the next day.
 *
 * An "overnight" range is one where the end time (HH:MM) is numerically less
 * than the start time — e.g. "22:00,02:00" starts at 10 PM and ends at 2 AM
 * the following day.
 *
 * Rules applied to each pipe-separated period:
 *   - Normal period (end >= start): stays on the current day unchanged.
 *   - Overnight period (end < start):
 *       • Current day receives start→23:59
 *       • Next day receives 00:00→end
 *
 * Returns:
 *   { currentDay: string, nextDay: string | null }
 *
 * Where `nextDay` is null when there are no overnight periods.
 *
 * Examples:
 *   "09:00,23:00"            → { currentDay: "09:00,23:00",   nextDay: null }
 *   "22:00,02:00"            → { currentDay: "22:00,23:59",   nextDay: "00:00,02:00" }
 *   "09:00,17:00|22:00,02:00"→ { currentDay: "09:00,17:00|22:00,23:59", nextDay: "00:00,02:00" }
 *   "00:00,00:01"            → { currentDay: "00:00,00:01",   nextDay: null }  (closed sentinel)
 */
export const splitOvernightValue = (
    value: string
): { currentDay: string; nextDay: string | null } => {
    // Pass closed sentinel through unchanged
    if (!value || value === '00:00,00:01') {
        return { currentDay: value, nextDay: null };
    }

    const periods = value.split('|');
    const currentDayPeriods: string[] = [];
    const nextDayPeriods: string[] = [];

    for (const period of periods) {
        const [rawStart, rawEnd] = period.split(',');
        const start = rawStart?.trim();
        const end = rawEnd?.trim();
        if (!start || !end) {
            currentDayPeriods.push(period);
            continue;
        }
        if (end === '00:00') {
            // Midnight end — treat as same-day period to avoid zero-length overflow
            currentDayPeriods.push(period);
        } else if (end < start) {
            // Overnight: split at midnight
            currentDayPeriods.push(`${start},23:59`);
            nextDayPeriods.push(`00:00,${end}`);
        } else {
            currentDayPeriods.push(period);
        }
    }

    return {
        currentDay: currentDayPeriods.join('|'),
        nextDay: nextDayPeriods.length > 0 ? nextDayPeriods.join('|') : null,
    };
};

/**
 * Extract the overnight prefix from a stored day value.
 *
 * An "overnight prefix" is a pipe-separated period where:
 *   • start is exactly "00:00"
 *   • end is between "00:02" and "06:00" inclusive
 *     (i.e. NOT the closed sentinel "00:00,00:01" and NOT past 6 AM)
 *
 * Only the first such period is treated as the overnight prefix (there
 * should never be more than one).
 *
 * The optional `previousDayValue` parameter tightens the heuristic: when
 * provided, a leading midnight period is only treated as an overnight prefix
 * if the previous day's value contains "23:59" (i.e. it has an overnight
 * range that was split at midnight).  This prevents real business hours that
 * start at midnight (e.g. "00:00,02:00") from being stripped when no
 * overnight period exists on the day before.
 *
 * Returns:
 *   { prefix: "00:00,HH:MM" | null, remainder: string }
 *
 * Where `remainder` is the original value with the prefix period removed.
 *
 * Examples:
 *   extractOvernightPrefix("00:00,02:20|09:00,17:00", "22:00,23:59")
 *     → { prefix: "00:00,02:20", remainder: "09:00,17:00" }
 *
 *   extractOvernightPrefix("00:00,02:00|09:00,17:00", "09:00,17:00")
 *     → { prefix: null, remainder: "00:00,02:00|09:00,17:00" }  (no 23:59 on prev day)
 *
 *   extractOvernightPrefix("09:00,17:00")
 *     → { prefix: null, remainder: "09:00,17:00" }
 *
 *   extractOvernightPrefix("00:00,00:01")           // closed sentinel
 *     → { prefix: null, remainder: "00:00,00:01" }
 *
 *   extractOvernightPrefix("00:00,07:00")           // past 06:00 – not overnight prefix
 *     → { prefix: null, remainder: "00:00,07:00" }
 */
export const extractOvernightPrefix = (
    value: string | undefined,
    previousDayValue?: string
): { prefix: string | null; remainder: string } => {
    if (!value || value === '00:00,00:01') {
        return { prefix: null, remainder: value ?? '' };
    }

    // When the caller supplies the previous day's raw value, only look for an
    // overnight prefix if that day actually has a period ending at 23:59 —
    // the sentinel written by splitOvernightValue.  This prevents legitimate
    // midnight-opening hours ("00:00,02:00") from being stripped as overflow.
    if (previousDayValue !== undefined && !previousDayValue.includes('23:59')) {
        return { prefix: null, remainder: value };
    }

    const periods = value.split('|');
    let prefix: string | null = null;
    const remainderPeriods: string[] = [];

    for (const period of periods) {
        const [start, end] = period.split(',');
        if (
            prefix === null &&
            start === '00:00' &&
            end &&
            end > '00:01' &&   // exclude closed sentinel
            end <= '06:00'     // only up to 6 AM counts as overnight overflow
        ) {
            prefix = period;
        } else {
            remainderPeriods.push(period);
        }
    }

    return {
        prefix,
        remainder: remainderPeriods.join('|'),
    };
};

/**
 * Reconstruct the original overnight display value from a current-day stored
 * value (which has a "23:59" sentinel) and the overnight prefix that was
 * written to the next day.
 *
 * When overnight hours are saved, `splitOvernightValue` replaces the overnight
 * period's end time with "23:59" on the current day and writes "00:00,HH:MM"
 * on the next day.  This function reverses that transformation so the UI can
 * display the true overnight end time again.
 *
 * Rules:
 *   - Find the last pipe-separated period in `currentDayValue` whose end is
 *     "23:59" — that is the overnight period.
 *   - Replace its end with the end from `overnightPrefix` (e.g. "02:20").
 *   - All other periods are left unchanged.
 *
 * The result is a standard backend overnight value (end < start in 24h),
 * which `SingleDayTimeRangeField.parseValue` already handles correctly —
 * it detects end < start and applies the "+1 (next day)" badge.
 *
 * Examples:
 *   reconstructOvernightDisplay("09:00,23:59", "00:00,02:20")
 *     → "09:00,02:20"
 *
 *   reconstructOvernightDisplay("09:00,17:00|21:00,23:59", "00:00,02:20")
 *     → "09:00,17:00|21:00,02:20"
 *
 *   reconstructOvernightDisplay("09:00,17:00", "00:00,02:20")
 *     → "09:00,17:00"   (no 23:59 sentinel found — value returned unchanged)
 */
export const reconstructOvernightDisplay = (
    currentDayValue: string,
    overnightPrefix: string
): string => {
    const prefixEnd = overnightPrefix.split(',')[1];
    if (!prefixEnd) return currentDayValue;

    const periods = currentDayValue.split('|');

    // Find the last period whose end is "23:59" — that is the overnight sentinel
    let replacedIndex = -1;
    for (let i = periods.length - 1; i >= 0; i--) {
        const end = periods[i].split(',')[1];
        if (end === '23:59') {
            replacedIndex = i;
            break;
        }
    }

    if (replacedIndex === -1) {
        // No sentinel found — nothing to reconstruct
        return currentDayValue;
    }

    const [overnightStart] = periods[replacedIndex].split(',');
    periods[replacedIndex] = `${overnightStart},${prefixEnd}`;
    return periods.join('|');
};

/**
 * Format timezone name/identifier into standard short code (e.g., America/New_York -> EST)
 */
export const formatTimeZone = (tz: string | null | undefined): string => {
    if (!tz) return '';
    const trimmed = tz.trim();
    if (!trimmed) return '';

    // If it's already an abbreviation (3-4 letters uppercase), use it directly
    if (trimmed.length <= 4 && /^[A-Z]{3,4}$/.test(trimmed)) {
        return trimmed;
    }

    // Try formatting with Intl.DateTimeFormat
    try {
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: trimmed,
            timeZoneName: 'short'
        });
        const parts = formatter.formatToParts(new Date());
        const tzPart = parts.find(p => p.type === 'timeZoneName');
        if (tzPart) {
            return tzPart.value;
        }
    } catch (e) {
        // Fallback for standard timezones if Intl fails
        const map: Record<string, string> = {
            'AMERICA/NEW_YORK': 'EST',
            'AMERICA/CHICAGO': 'CST',
            'AMERICA/DENVER': 'MST',
            'AMERICA/LOS_ANGELES': 'PST',
            'AMERICA/PHOENIX': 'MST',
            'AMERICA/ANCHORAGE': 'AKST',
            'AMERICA/ADAK': 'HST',
            'AMERICA/HONOLULU': 'HST',
            'EUROPE/LONDON': 'GMT',
            'UTC': 'UTC',
            'US/EASTERN': 'EST',
            'US/CENTRAL': 'CST',
            'US/MOUNTAIN': 'MST',
            'US/PACIFIC': 'PST',
            'EASTERN STANDARD TIME': 'EST',
            'EASTERN DAYLIGHT TIME': 'EDT',
            'CENTRAL STANDARD TIME': 'CST',
            'CENTRAL DAYLIGHT TIME': 'CDT',
            'MOUNTAIN STANDARD TIME': 'MST',
            'MOUNTAIN DAYLIGHT TIME': 'MDT',
            'PACIFIC STANDARD TIME': 'PST',
            'PACIFIC DAYLIGHT TIME': 'PDT',
            'EASTERN': 'EST',
            'CENTRAL': 'CST',
            'MOUNTAIN': 'MST',
            'PACIFIC': 'PST'
        };
        const upperTrimmed = trimmed.toUpperCase();
        if (map[upperTrimmed]) {
            return map[upperTrimmed];
        }
    }
    return trimmed;
};

/**
 * Format a 24-hour time to short readable format, e.g. "09:00" -> "9AM", "17:30" -> "5:30PM"
 */
export const formatSingleTime = (time24: string): string => {
    const parts = time24.split(':');
    if (parts.length < 2) return time24;
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (isNaN(h) || isNaN(m)) return time24;

    const ampm = h >= 12 ? 'PM' : 'AM';
    let displayHour = h % 12;
    if (displayHour === 0) displayHour = 12;
    const displayMinute = m === 0 ? '' : `:${parts[1].padStart(2, '0')}`;
    return `${displayHour}${displayMinute}${ampm}`;
};

/**
 * Format today's hours and timezone into a human-readable string like "9AM - 5PM EST" or "Closed"
 */
export const formatHoursToday = (
    hoursStr: string | null | undefined,
    timeZones: string[] | undefined
): string => {
    if (!hoursStr || hoursStr === 'None') return 'None';
    if (hoursStr === '00:00,00:01') return 'Closed';

    const periods = hoursStr.split('|');
    const formattedPeriods = periods.map(period => {
        const parts = period.split(',');
        if (parts.length !== 2) return period;
        const startFormatted = formatSingleTime(parts[0].trim());
        const endFormatted = formatSingleTime(parts[1].trim());
        return `${startFormatted} - ${endFormatted}`;
    });

    const hoursDisplay = formattedPeriods.join(', ');

    const rawTz = timeZones && timeZones.length > 0 ? timeZones[0] : '';
    const tzDisplay = formatTimeZone(rawTz);

    if (tzDisplay) {
        return `${hoursDisplay} ${tzDisplay}`;
    }
    return hoursDisplay;
};

/**
 * Extract the earliest start time in minutes from midnight for sorting.
 * Returns:
 *   - minutes from midnight (0 to 1439) for valid open times
 *   - 10000 for Closed
 *   - 20000 for None/unconfigured
 */
export const getEarliestStartMinutes = (hoursStr: string | null | undefined): number => {
    if (!hoursStr || hoursStr === 'None') return 20000;
    if (hoursStr === '00:00,00:01') return 10000;

    // Strip overnight rollover prefix from previous day
    const { remainder } = extractOvernightPrefix(hoursStr);
    const useStr = remainder.trim() || hoursStr; // Fallback to raw if remainder becomes completely empty

    if (useStr === '00:00,00:01') return 10000;

    const periods = useStr.split('|');
    let earliestMinutes = Infinity;

    for (const period of periods) {
        const parts = period.split(',');
        if (parts.length === 2) {
            const timeParts = parts[0].trim().split(':');
            if (timeParts.length >= 2) {
                const h = parseInt(timeParts[0], 10);
                const m = parseInt(timeParts[1], 10);
                if (!isNaN(h) && !isNaN(m)) {
                    const minutes = h * 60 + m;
                    if (minutes < earliestMinutes) {
                        earliestMinutes = minutes;
                    }
                }
            }
        }
    }

    if (earliestMinutes === Infinity) {
        return 20000; // Fallback if no valid period parsed
    }

    return earliestMinutes;
};
